import React, { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import { Text } from 'components/Typography';
import { CheckModifiedDetails, ICheck, ITipCalculatorChangeLogEntry } from 'types';
import { centsToDollarsDisplay, formatDateForUILongFormat } from 'utils';
import Sidebar, { ISidebarNotification } from './components/Sidebar';
import { Content, ContentSection, RowItem } from './components/styles';

interface ITipCalcChecksSidebarProps {
  dataCy?: string;
  onBack: () => void;
  onContinue: () => void;
  businessDate: Dayjs;
  total: number;
  ignoredChecks?: ICheck[];
  splitChecks?: ICheck[];
  continueDisabled?: boolean;
  cashEditChangeLogEntries?: ITipCalculatorChangeLogEntry[];
  notifications?: ISidebarNotification[];
}

const TipCalcChecksSidebar = ({ businessDate, total, ignoredChecks, splitChecks, cashEditChangeLogEntries, notifications, onBack, onContinue, continueDisabled = false, dataCy = 'tip-calc-checks-sidebar' }: ITipCalcChecksSidebarProps) => {
  const ignoredTotal = useMemo(() => ignoredChecks?.reduce((acc, check) => acc + check.totals.gross, 0) ?? 0, [ignoredChecks]);
  return (
    <Sidebar
      title="Check Summary"
      leftActionButton={{ label: 'Back', appearance: 'white', onClick: onBack }}
      rightActionButton={{ label: 'Recalculate', appearance: 'primary', disabled: continueDisabled || notifications?.some((notification) => notification.preventSubmit), onClick: onContinue }}
      notifications={notifications}
      dataCy={dataCy}
    >
      <Content>
        <RowItem data-cy={`${dataCy}-business-date`} className="business-date">
          <Text size="small" data-cy={`${dataCy}-business-date-label`}>
            Business Date:
          </Text>
          <Text size="small" data-cy={`${dataCy}-business-date-value`}>
            {formatDateForUILongFormat(businessDate)}
          </Text>
        </RowItem>
        <RowItem data-cy={`${dataCy}-total`} className="total">
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-total-label`}>
            Collected Tips & Autograts:
          </Text>
          <Text size="small" fontWeight="medium" data-cy={`${dataCy}-total-value`}>
            {centsToDollarsDisplay(total)}
          </Text>
        </RowItem>
        {ignoredChecks?.length > 0 && (
          <>
            <ContentSection data-cy={`${dataCy}-summary-section`}>
              <Text size="small" fontWeight="medium" data-cy={`${dataCy}-summary-section-details-header`}>
                Details:
              </Text>
              <RowItem>
                <Text size="small" data-cy={`${dataCy}-summary-section-ignored-label`}>
                  Ignored
                </Text>
                <Text size="small" data-cy={`${dataCy}-summary-section-ignored-value`}>
                  {centsToDollarsDisplay(ignoredTotal)}
                </Text>
              </RowItem>
              <RowItem>
                <Text size="small" data-cy={`${dataCy}-summary-section-tips-to-be-paid-label`}>
                  Tips To Be Paid
                </Text>
                <Text size="small" data-cy={`${dataCy}-summary-section-tips-to-be-paid-value`}>
                  {centsToDollarsDisplay(total - ignoredTotal)}
                </Text>
              </RowItem>
            </ContentSection>
            <ContentSection data-cy={`${dataCy}-ignored-checks-section`}>
              <Text size="small" fontWeight="medium" data-cy={`${dataCy}-ignored-checks-header`}>
                Ignored Checks:
              </Text>
              {ignoredChecks.map((check) => (
                <RowItem key={`${dataCy}-ignored-checks-${check.key}`} data-cy={`${dataCy}-ignored-checks-${check.ticketId}`}>
                  <Text size="small" data-cy={`${dataCy}-ignored-checks-label-${check.ticketId}`}>
                    #{check.displayId}
                  </Text>
                  <Text size="small" data-cy={`${dataCy}-ignored-checks-value-${check.ticketId}`}>
                    {centsToDollarsDisplay(check.totals.gross)}
                  </Text>
                </RowItem>
              ))}
            </ContentSection>
          </>
        )}
        {splitChecks?.length > 0 && (
          <ContentSection data-cy={`${dataCy}-split-checks-section`}>
            <Text size="small" fontWeight="medium" data-cy={`${dataCy}-split-checks-header`}>
              Split Checks:
            </Text>
            {splitChecks.map((check) => (
              <RowItem key={`${dataCy}-split-checks-${check.key}`} data-cy={`${dataCy}-split-checks-${check.ticketId}`}>
                <Text size="small" data-cy={`${dataCy}-split-checks-label-${check.ticketId}`}>
                  #{check.displayId}
                </Text>
                <Text size="small" data-cy={`${dataCy}-split-checks-value-${check.ticketId}`}>
                  {centsToDollarsDisplay(check.totals.gross)}
                </Text>
              </RowItem>
            ))}
          </ContentSection>
        )}
        {cashEditChangeLogEntries?.length > 0 && (
          <ContentSection data-cy={`${dataCy}-cash-edit-change-log-entries-section`}>
            <Text size="small" fontWeight="medium" data-cy={`${dataCy}-cash-edit-change-log-entries-header`}>
              Cash Tips Updates
            </Text>
            {cashEditChangeLogEntries.map((entry, index) => (
              <RowItem key={`${dataCy}-cash-edit-change-log-entries-${index}`} data-cy={`${dataCy}-cash-edit-change-log-entries-${index}`}>
                <Text size="small" data-cy={`${dataCy}-cash-edit-change-log-entries-label-${index}`}>
                  #{(entry.details as CheckModifiedDetails)?.displayId}
                </Text>
                <Text size="small" data-cy={`${dataCy}-cash-edit-change-log-entries-value-${index}`}>
                  {centsToDollarsDisplay((entry.details as CheckModifiedDetails)?.to)}
                </Text>
              </RowItem>
            ))}
          </ContentSection>
        )}
      </Content>
    </Sidebar>
  );
};

export default TipCalcChecksSidebar;
